/**
 * The reset-password screen (epic 103 D4, task 04) — the unauthenticated
 * completion leg, mounted OUTSIDE the Layout beside /login. The raw token
 * arrives in the query string (`/reset-password?token=…` from the email
 * link); only the api's sha256 lookup can judge it, so nothing about it is
 * validated client-side beyond presence.
 *
 * Frames: missing token → an invalid-link frame (never a white screen —
 * the #137 lesson); success (204) → a "password changed" frame linking
 * /login — no auto-login, the user signs in (D4); every failure the api
 * can answer (unknown/expired/used token, inactive user) is one generic
 * 400, surfaced as an invalid-or-expired-link frame offering a NEW
 * request — the same token is never retried automatically. Client-side
 * validation mirrors the api's whole policy: non-empty and matching.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

/** Outcome of the submit. `changed`: the api's 204 — done. `invalidLink`:
 * the api's one generic 400 (unknown/expired/used token, inactive user) —
 * never retried with the same token. `failed`: network failure or 5xx —
 * stated honestly; a retry with the same token stays allowed (the fate of
 * the token is unknown, only the api can judge it). */
type ResetOutcome = 'changed' | 'invalidLink' | 'failed';

async function submitResetPassword(token: string, newPassword: string): Promise<ResetOutcome> {
  try {
    const response = await apiFetch(`${API_BASE_URL}/admin/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
      skipAuthRedirect: true,
    });
    if (response.ok) {
      return 'changed';
    }
    return response.status === 400 ? 'invalidLink' : 'failed';
  } catch {
    return 'failed';
  }
}

/**
 * Field-level submit hints, or null when the field would pass. Both fields
 * are checked raw — the api's whole policy is `min(1)` (no strength rules,
 * task 02 contract), and the match check is the screen's own.
 */
function passwordHints(
  newPassword: string,
  confirmPassword: string
): { newPassword: string | null; confirmPassword: string | null } {
  if (newPassword === '') {
    return { newPassword: 'Enter a new password.', confirmPassword: null };
  }
  if (confirmPassword === '') {
    return { newPassword: null, confirmPassword: 'Confirm your new password.' };
  }
  if (newPassword !== confirmPassword) {
    return { newPassword: null, confirmPassword: 'Passwords do not match.' };
  }
  return { newPassword: null, confirmPassword: null };
}

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [hints, setHints] = useState<{
    newPassword: string | null;
    confirmPassword: string | null;
  }>({ newPassword: null, confirmPassword: null });
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<ResetOutcome | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending) return;
    const nextHints = passwordHints(newPassword, confirmPassword);
    if (nextHints.newPassword !== null || nextHints.confirmPassword !== null) {
      setHints(nextHints);
      return;
    }
    setPending(true);
    setOutcome(null);
    try {
      const result = await submitResetPassword(token, newPassword);
      setOutcome(result);
    } finally {
      setPending(false);
    }
  };

  /** The shared brand header of the auth screens (login/change-password). */
  const brandHeader = (
    <div className="mb-6 flex items-center justify-center gap-2.5">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent font-display text-[16px] font-bold text-white">
        T
      </div>
      <div className="leading-tight">
        <div className="font-display text-[16px] font-semibold text-ink">TradingTips</div>
        <div className="font-mono text-xs uppercase tracking-[0.16em] text-muted">
          Lead Ops Console
        </div>
      </div>
    </div>
  );

  if (token === '') {
    return (
      <div className="grain flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-sm">
          {brandHeader}
          <Card>
            <CardHeader>
              <CardTitle>Invalid reset link</CardTitle>
              <CardDescription>
                This link is missing its token — it may have been cut off.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                variant="primary"
                className="w-full"
                onClick={() => navigate('/forgot-password')}
              >
                Request a new reset link
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (outcome === 'changed') {
    return (
      <div className="grain flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-sm">
          {brandHeader}
          <Card>
            <CardHeader>
              <CardTitle>Password changed</CardTitle>
              <CardDescription>Your new password is ready — sign in with it.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                variant="primary"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Sign in
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (outcome === 'invalidLink') {
    return (
      <div className="grain flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-sm">
          {brandHeader}
          <Card>
            <CardHeader>
              <CardTitle>Invalid or expired link</CardTitle>
              <CardDescription>
                This reset link has already been used or has expired.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-[13px] text-muted">
                  Reset links work once and expire after a short time. Request a new one to try
                  again.
                </p>
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  onClick={() => navigate('/forgot-password')}
                >
                  Request a new reset link
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="grain flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {brandHeader}
        <Card>
          <CardHeader>
            <CardTitle>Set a new password</CardTitle>
            <CardDescription>Choose the new password for your admin account.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-4">
                {outcome === 'failed' && (
                  <p role="alert" className="text-[13px] text-destructive">
                    The request failed. Check your connection and try again.
                  </p>
                )}
                <div className="space-y-2">
                  <Label htmlFor="new-password">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setHints((current) => ({ ...current, newPassword: null }));
                    }}
                    aria-invalid={hints.newPassword !== null}
                    aria-describedby={hints.newPassword !== null ? 'new-password-hint' : undefined}
                    required
                    autoFocus
                  />
                  {hints.newPassword !== null && (
                    <p id="new-password-hint" className="text-[13px] text-destructive">
                      {hints.newPassword}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setHints((current) => ({ ...current, confirmPassword: null }));
                    }}
                    aria-invalid={hints.confirmPassword !== null}
                    aria-describedby={
                      hints.confirmPassword !== null ? 'confirm-password-hint' : undefined
                    }
                    required
                  />
                  {hints.confirmPassword !== null && (
                    <p id="confirm-password-hint" className="text-[13px] text-destructive">
                      {hints.confirmPassword}
                    </p>
                  )}
                </div>
                <div className="w-full pt-1">
                  <Button type="submit" variant="primary" className="w-full" disabled={pending}>
                    {pending ? 'Saving...' : 'Save new password'}
                  </Button>
                </div>
                <div className="pt-1 text-center">
                  <Link
                    to="/login"
                    className="text-[13px] text-muted underline underline-offset-4 hover:text-ink"
                  >
                    Back to sign in
                  </Link>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
